// PhaseBriefingPanel — full M3.1 panel.
//
// Features (plan §M3.1):
//   - "What I'm about to do" / "Why" / cost-estimate sections (from the
//     briefing markdown artifact).
//   - Delta-briefing badge when the backend picked delta mode (§M3.3).
//   - Three actions: Approve · Edit · Reject (rewind). Labels mirror the
//     gate triad so the user sees one vocabulary across briefing + G1–G5
//     (plan §P0.1). Internal action keys (`approve`/`modify`/`go_back`)
//     stay unchanged so the API contract is preserved.
//   - Edit reveals a text field; Reject reveals a phase picker with the
//     allowed destinations + a required reason textarea.
//   - Run Phase 1 button after approval.
//
// UX v0.7 (plan §P1.7) — the legacy shadcn ``<Card>`` shell has been
// migrated to the canonical ``alm-*`` design language so the user
// stops jumping between two parallel design systems when they enter
// the Command Center. The decision affordances now use the same
// ``alm-decision`` triad the gate panels use, the chips read
// ``Approve / Edit / Reject`` (matching G1–G5), and the surrounding
// chrome uses ``alm-card`` / ``PageHeader`` / ``PrimaryButton`` to
// share styling with the rest of the focus pane.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Edit3,
  FileText,
  Loader2,
  PencilLine,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react'

import { toast } from '@/hooks/use-toast'

import {
  ActionBar,
  EmptyState,
  PageHeader,
  PrimaryButton,
  Section,
  SecondaryButton,
} from '@/components/ui/shell'
import { api, ApiError, projectFileUrl } from '@/lib/api'
import { waitForPhaseCompletion } from '@/lib/phaseEvents'
import { stripFrontmatter } from '@/lib/markdown'

// Valid go-back destinations per source phase (plan §M3.3).
const ALLOWED_GOTO: Record<number, number[]> = {
  1: [],
  2: [1],
  3: [1, 2],
  4: [1, 2, 3],
  5: [1, 2, 3, 4],
}

type DecisionKind = null | 'approve' | 'modify' | 'go_back'

export interface PhaseBriefingPanelProps {
  projectId: string
  runId?: number
  phase?: number
  onApproved?: () => void
  /**
   * Fired when the phase run kicked off from this briefing completes.
   * The Command Center uses this to auto-advance focus to the next
   * surface (Gate G1) instead of leaving the user stranded on the
   * briefing with a "look below" toast (P1/G1 UX fix plan §A2).
   */
  onPhaseComplete?: () => void
}

export function PhaseBriefingPanel({
  projectId,
  runId = 1,
  phase = 1,
  onApproved,
  onPhaseComplete,
}: PhaseBriefingPanelProps) {
  const [decisionKind, setDecisionKind] = useState<DecisionKind>(null)
  const [modification, setModification] = useState('')
  const [gotoPhase, setGotoPhase] = useState<number | null>(null)
  const [rewindReason, setRewindReason] = useState('')
  const [briefingBody, setBriefingBody] = useState<string>('')

  const briefing = useQuery({
    queryKey: ['briefing', projectId, runId, phase],
    queryFn: () => api.getBriefing(projectId, runId, phase),
    retry: false,
  })

  // Load the markdown artifact when the briefing record is available so we
  // can render the "What I'm about to do" / "Why" sections.
  useEffect(() => {
    if (!briefing.data?.artifact_path) {
      setBriefingBody('')
      return
    }
    fetch(projectFileUrl(projectId, briefing.data.artifact_path))
      .then((res) => (res.ok ? res.text() : ''))
      .then((txt) => setBriefingBody(txt))
      .catch(() => setBriefingBody(''))
  }, [briefing.data?.artifact_path, projectId])

  const queryClient = useQueryClient()

  const emit = useMutation({
    mutationFn: () => api.createBriefing(projectId, runId, phase),
    onSuccess: () => briefing.refetch(),
  })

  const approve = useMutation({
    mutationFn: () =>
      api.submitBriefingDecision(projectId, runId, phase, { action: 'approve' }),
    onSuccess: () => {
      briefing.refetch()
      setDecisionKind(null)
      onApproved?.()
    },
  })

  // Hold on to the last modification text the user submitted so the
  // "Re-run with modification" follow-up button can use it. Without
  // this the modify decision was a write-into-a-void: the modification
  // got recorded but never made it into a Phase-1 re-run.
  const [pendingModification, setPendingModification] = useState<
    string | null
  >(null)
  const modify = useMutation({
    mutationFn: () =>
      api.submitBriefingDecision(projectId, runId, phase, {
        action: 'modify',
        modification: modification.trim() || null,
      }),
    onSuccess: () => {
      briefing.refetch()
      setPendingModification(modification.trim() || null)
      setDecisionKind(null)
    },
  })

  const goBack = useMutation({
    mutationFn: () => {
      if (gotoPhase === null) {
        throw new Error('Choose a destination phase.')
      }
      if (!rewindReason.trim()) {
        throw new Error('A free-text reason is required for rewinds (plan §M3.3).')
      }
      return api.submitBriefingDecision(projectId, runId, phase, {
        action: 'go_back',
        goto_phase: gotoPhase,
        reason: rewindReason.trim(),
      })
    },
    onSuccess: () => {
      briefing.refetch()
      setDecisionKind(null)
    },
  })

  // Phase-1 runner (M3 only wires phase 1 here; later phases run from
  // PipelineRunner / GateReviewPanel). When the user just submitted a
  // "modify" decision we forward the modification text into Phase 1 as
  // ``go_back_reason`` so the Contextualist sees it on the new run.
  const runPhase = useMutation({
    mutationFn: async () => {
      // ``runPhase1`` now returns 202 the moment the backend accepts the
      // job (``?async=true``); the real completion arrives as a
      // ``phase_completed`` WS event. Await it before treating the run as
      // done so the synchronous HTTP request no longer outlives the load
      // balancer's timeout. ``waitForPhaseCompletion`` also polls
      // run-state as a fallback if the WS event is missed.
      await api.runPhase1(projectId, runId, {
        go_back_reason: pendingModification ?? undefined,
      })
      return waitForPhaseCompletion(projectId, runId, 1)
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: `Phase ${phase} complete`,
        description:
          'Charter, Intent Map, and Glossary were written — opening Gate G1.',
      })
      // Invalidate everything that depends on Phase-1 outputs so the
      // gate panel + pipeline runner refetch their state.
      queryClient.invalidateQueries({ queryKey: ['gate', projectId, runId, 1] })
      queryClient.invalidateQueries({
        queryKey: ['artifact-index', projectId, runId],
      })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      // Re-emit the briefing artifact so the next decision starts fresh.
      setPendingModification(null)
      // Auto-advance the Command Center focus to Gate G1 (P1/G1 UX fix
      // plan §A2) — the review lives on a different surface, so telling
      // the user to "look below" was a wrong-direction cue.
      onPhaseComplete?.()
    },
  })

  // ``runPhase`` now resolves with the ``phase_completed`` WS event rather
  // than the old synchronous Phase1RunResponse. The written artifact paths
  // ride on its payload (same ``as_state_map`` shape: charter / intent_map
  // / glossary), so the success panel reads them from there.
  const phase1Artifacts =
    (runPhase.data?.payload?.artifacts as Record<string, string> | undefined) ??
    null

  const allowedDestinations = ALLOWED_GOTO[phase] ?? []
  const isMissing =
    briefing.error instanceof ApiError && briefing.error.status === 404
  const isDecided =
    briefing.data?.status === 'approved' ||
    briefing.data?.status === 'modified' ||
    briefing.data?.status === 'go_back'

  const { whatText, whyText, costText } = useMemo(
    () => parseBriefingSections(briefingBody),
    [briefingBody],
  )

  const headerChips = (
    <>
      <span className="alm-chip alm-chip-warn">
        <AlertCircle className="h-3 w-3" /> Needs you
      </span>
      <span className="alm-chip">Phase {phase} briefing</span>
      {briefing.data?.briefing_type === 'delta' ? (
        <span className="alm-chip alm-chip-info">Delta</span>
      ) : null}
      {briefing.data?.status ? (
        <span className="alm-chip">{briefing.data.status}</span>
      ) : null}
    </>
  )

  return (
    // E2E hook (T2 — plan §5.5 / check-testids.mjs). One root per
    // briefing card; the phase number suffix lets specs target a
    // specific briefing without a stable text query.
    <div data-testid={`briefing-${phase}-card`} className="alm-fadein">
      <PageHeader
        chips={headerChips}
        title={`Phase ${phase} Briefing`}
        subtitle={`Review before Phase ${phase} spends any LLM tokens.`}
        trailing={
          <>
            <SecondaryButton
              size="sm"
              onClick={() => emit.mutate()}
              loading={emit.isPending}
              icon={<RefreshCw className="h-3 w-3" />}
            >
              Re-emit
            </SecondaryButton>
            {briefing.data?.artifact_path ? (
              <a
                href={projectFileUrl(projectId, briefing.data.artifact_path)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-400 hover:bg-white/5"
              >
                <FileText className="h-3 w-3" /> Raw artifact
              </a>
            ) : null}
          </>
        }
      />

      {briefing.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading briefing…
        </div>
      ) : null}

      {isMissing ? (
        <div className="mb-5">
          <EmptyState
            icon={<AlertCircle className="h-5 w-5 text-slate-500" />}
            title="No briefing yet"
            description={
              <>
                Click <strong>Re-emit</strong> to generate the Phase-{phase}{' '}
                briefing — it's a deterministic summary, no LLM spend.
              </>
            }
          />
        </div>
      ) : null}

      {briefing.data ? (
        <>
          {/* Rendered briefing sections from the markdown artifact */}
          {whatText ? (
            <div className="alm-card mb-3">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1.5">
                What I'm about to do
              </div>
              <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                {whatText}
              </div>
            </div>
          ) : null}
          {whyText ? (
            <div className="alm-card mb-3">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1.5">
                Why
              </div>
              <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                {whyText}
              </div>
            </div>
          ) : null}
          {costText ? (
            <div className="alm-card mb-3">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1.5">
                Cost & duration
              </div>
              <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                {costText}
              </div>
            </div>
          ) : null}

          <p className="text-[11px] text-slate-500 mb-4 font-mono">
            Artifact: {briefing.data.artifact_path}
          </p>

          {/* Decision triad — uses the same ``alm-decision`` affordance
              as G1–G5 so the user sees one vocabulary across the
              workflow (plan §P0.1). Note the canonical labels are
              "Approve / Edit / Reject" — the underlying mutation keys
              (`approve` / `modify` / `go_back`) are unchanged. */}
          {!isDecided ? (
            <Section label="Your decision" className="mb-4">
              <div className="flex flex-wrap gap-2">
                {/* P1/G1 UX fix plan §B2 — approval spends zero tokens,
                    so it's a single click. The confirm step now lives on
                    the actions that DO spend (Run Phase 1). */}
                <button
                  type="button"
                  onClick={() => approve.mutate()}
                  disabled={approve.isPending}
                  className="alm-decision"
                  data-selected={approve.isPending ? 'approved' : undefined}
                  title="Approve this briefing (no LLM spend) — the Run Phase button appears next."
                >
                  {approve.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3" />
                  )}{' '}
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setDecisionKind('modify')}
                  className="alm-decision"
                  data-selected={decisionKind === 'modify' ? 'edited' : undefined}
                  title="Edit the briefing notes and re-run this phase with your modification."
                >
                  <Edit3 className="h-3 w-3" /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => setDecisionKind('go_back')}
                  disabled={allowedDestinations.length === 0}
                  className="alm-decision"
                  data-selected={decisionKind === 'go_back' ? 'rejected' : undefined}
                  title={
                    allowedDestinations.length === 0
                      ? 'Phase 1 has no valid rewind target — project just started.'
                      : 'Reject this briefing and rewind to an earlier phase.'
                  }
                >
                  <X className="h-3 w-3" /> Reject
                </button>
              </div>
            </Section>
          ) : null}

          {/* Modify form */}
          {decisionKind === 'modify' ? (
            <div className="alm-card mb-5 space-y-2">
              <label
                htmlFor={`modify-${phase}`}
                className="text-[10.5px] uppercase tracking-wider text-slate-500"
              >
                Modification notes
              </label>
              <textarea
                id={`modify-${phase}`}
                rows={3}
                placeholder="Describe the change you'd like the orchestrator to apply…"
                value={modification}
                onChange={(e) => setModification(e.target.value)}
                className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
              />
              <ActionBar>
                <PrimaryButton
                  onClick={() => modify.mutate()}
                  loading={modify.isPending}
                  icon={<PencilLine className="h-4 w-4" />}
                >
                  Submit edit
                </PrimaryButton>
                <SecondaryButton onClick={() => setDecisionKind(null)}>
                  Cancel
                </SecondaryButton>
              </ActionBar>
            </div>
          ) : null}

          {/* Go-back form */}
          {decisionKind === 'go_back' && allowedDestinations.length > 0 ? (
            <div className="alm-card mb-5 space-y-3">
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-1.5">
                  Rewind destination
                </div>
                <div className="flex flex-wrap gap-2">
                  {allowedDestinations.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setGotoPhase(p)}
                      className="alm-decision"
                      data-selected={gotoPhase === p ? 'edited' : undefined}
                    >
                      Re-enter at Phase {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label
                  htmlFor={`reason-${phase}`}
                  className="text-[10.5px] uppercase tracking-wider text-slate-500"
                >
                  Reason (required — plan §M3.3)
                </label>
                <textarea
                  id={`reason-${phase}`}
                  rows={2}
                  placeholder="Why is the rewind needed?"
                  value={rewindReason}
                  onChange={(e) => setRewindReason(e.target.value)}
                  className="mt-1 w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
                />
              </div>
              <ActionBar>
                <PrimaryButton
                  onClick={() => goBack.mutate()}
                  loading={goBack.isPending}
                  icon={<ArrowLeft className="h-4 w-4" />}
                >
                  Confirm rewind
                </PrimaryButton>
                <SecondaryButton onClick={() => setDecisionKind(null)}>
                  Cancel
                </SecondaryButton>
              </ActionBar>
              {goBack.error ? (
                <p className="text-xs text-red-300" role="alert">
                  {(goBack.error as Error).message}
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Modified-status flow: surfaces a clear "Re-run with
              modification" CTA so the modification text actually feeds
              the next Phase-1 pass. */}
          {briefing.data.status === 'modified' && phase === 1 ? (
            <div className="rounded-lg border border-amber-700/50 bg-amber-950/20 p-4 mb-5 text-sm text-amber-100 space-y-2">
              <div className="flex items-center gap-2">
                <PencilLine className="h-4 w-4 text-amber-300" />
                <strong>Modification recorded</strong>
              </div>
              <p className="text-xs text-amber-200/80 leading-relaxed">
                Re-run Phase 1 to apply your modification to the
                Contextualist's next pass.
              </p>
              {pendingModification ? (
                <blockquote className="border-l-2 border-amber-700/50 pl-3 text-xs text-amber-100/90 italic">
                  {pendingModification}
                </blockquote>
              ) : null}
              <PrimaryButton
                onClick={() => runPhase.mutate()}
                loading={runPhase.isPending}
                icon={<Sparkles className="h-4 w-4" />}
                className="mt-1"
              >
                Re-run Phase 1 with modification
              </PrimaryButton>
            </div>
          ) : null}

          {/* Run Phase after approval (Phase 1 only in M3) */}
          {briefing.data.status === 'approved' && phase === 1 ? (
            <ActionBar className="mb-3">
              <PrimaryButton
                // E2E hook (T2 — plan §5.5). Phase 1's run button
                // lives on the briefing card (PipelineRunner stops
                // at Phase 2+); this testid lets the shared
                // ``CommandCenterPage.clickPhaseRun(1)`` POM target
                // it the same way it targets the later phases'
                // pipeline-runner buttons.
                data-testid={`phase-${phase}-run-button`}
                data-state={runPhase.isPending ? 'running' : 'ready'}
                onClick={() => runPhase.mutate()}
                loading={runPhase.isPending}
                icon={<ArrowRight className="h-4 w-4" />}
              >
                {runPhase.isPending
                  ? `Running Phase ${phase}… (~60 s)`
                  : `Run Phase ${phase}`}
              </PrimaryButton>
            </ActionBar>
          ) : null}

          {briefing.data.status === 'approved' && phase === 1 && runPhase.isPending ? (
            <p className="mt-1 mb-3 text-xs text-slate-500">
              The Contextualist is thinking — typically 40–60 seconds at
              high effort. See the briefing's Cost &amp; Duration block above
              for the active provider and model.
            </p>
          ) : null}

          {phase1Artifacts ? (
            <div className="rounded-md border border-emerald-700/40 bg-emerald-950/20 p-3 text-xs text-emerald-200 mt-3">
              <strong className="text-emerald-300">
                Phase {phase} complete.
              </strong>{' '}
              Artifacts written to{' '}
              <code className="font-mono">{phase1Artifacts.charter}</code>,{' '}
              <code className="font-mono">{phase1Artifacts.intent_map}</code>, and{' '}
              <code className="font-mono">{phase1Artifacts.glossary}</code>.
            </div>
          ) : null}

          {runPhase.error ? (
            <div
              role="alert"
              className="rounded-md border border-red-700/40 bg-red-950/20 p-3 text-xs text-red-200 mt-3"
            >
              <strong className="text-red-300">Phase {phase} failed.</strong>{' '}
              {(runPhase.error as ApiError).message}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}


// --------------------------------------------------------------------------- //
// Parsers
// --------------------------------------------------------------------------- //
/**
 * Pluck the three core sections out of the briefing markdown so the UI can
 * surface them without re-rendering the whole markdown tree.
 */
export function parseBriefingSections(markdown: string): {
  whatText: string
  whyText: string
  costText: string
} {
  if (!markdown) return { whatText: '', whyText: '', costText: '' }

  const stripped = stripFrontmatter(markdown)
  const sections: Record<string, string> = {}
  const regex = /^##\s+([^\n]+)\n([\s\S]*?)(?=^##\s+|\Z)/gm
  let match
  while ((match = regex.exec(stripped)) !== null) {
    const [, heading, body] = match
    sections[heading.trim().toLowerCase()] = body.trim()
  }
  return {
    whatText: sections["what i'm about to do"] ?? '',
    whyText: sections['why'] ?? '',
    costText: sections['cost & duration'] ?? '',
  }
}
